import type {
  ConversationMessage,
  DisplayedError,
  DisplayedInformation,
  SgwtChatbotConversation,
  SgwtChatbotConversationItem,
  SgwtChatbotMessage,
  UserFeedback,
  UserFeedbackReaction,
} from './chatbot.types';

export interface BotConversationReducerState {
  conversationId: string;
  history: SgwtChatbotConversation[] | undefined;
  messages: SgwtChatbotConversationItem[];
  waitingBot: boolean;
}

const generateId = () => crypto.randomUUID();

type BotConversationReducerAction =
  | { type: 'chatbot.waiting-for-bot' }
  | { type: 'chatbot.error-occured'; payload: Error }
  | { type: 'information.display'; payload: string }
  // prompt & response
  | { type: 'chatbot.send-message'; payload: SgwtChatbotMessage }
  | { type: 'chatbot.receive-message'; payload: { response: SgwtChatbotMessage; allowFeedback: boolean } }

  // History management
  // Set history entries
  | { type: 'chatbot.set-conversations-history'; payload: SgwtChatbotConversation[] }
  // Start a new conversation
  | { type: 'chatbot.start-new-conversation' }
  // Switch to an existing conversation from the history
  | { type: 'chatbot.switch-conversation'; payload: SgwtChatbotConversation }

  // User interactions
  // Ations related to user reaction (like / dislike)
  | { type: 'feedback.requested'; payload: { reaction: UserFeedbackReaction; message: SgwtChatbotMessage } }
  | { type: 'feedback.cancelled'; payload: UserFeedback }
  | { type: 'feedback.submitted'; payload: UserFeedback };

export function botConversationReducer(currentState: BotConversationReducerState, action: BotConversationReducerAction): BotConversationReducerState {
  switch (action.type) {
    // We are waiting for the bot to respond
    case 'chatbot.waiting-for-bot': {
      return {
        ...currentState,
        waitingBot: true,
      };
    }

    // User sends a message to the chatbot
    case 'chatbot.send-message': {
      const userMessage: ConversationMessage = {
        _id: generateId(),
        metadata: {
          feedbackAllowed: false,
        },
        type: 'message',
        content: {
          ...action.payload,
        },
      };

      return {
        ...currentState,
        waitingBot: true,
        messages: [...currentState.messages, userMessage],
      };
    }

    // Bot responds to the user's message
    case 'chatbot.receive-message': {
      const messageReceived: ConversationMessage = {
        _id: generateId(),
        type: 'message',
        content: {
          ...action.payload.response,
        },
        metadata: {
          feedbackAllowed: action.payload.allowFeedback,
        },
      };

      return {
        ...currentState,
        conversationId: messageReceived.content.conversationId ?? '',
        waitingBot: false,
        messages: [...currentState.messages, messageReceived],
      };
    }

    // Display an information to the user
    case 'information.display': {
      const messageReceived: DisplayedInformation = {
        type: 'information',
        _id: generateId(),
        message: {
          content: action.payload,
          format: 'text',
        },
      };

      return {
        ...currentState,
        messages: [...currentState.messages, messageReceived],
      };
    }

    // An error occured during the conversation
    case 'chatbot.error-occured': {
      const messageReceived: DisplayedError = {
        type: 'error',
        _id: generateId(),
        error: action.payload,
      };

      return {
        ...currentState,
        messages: [...currentState.messages, messageReceived],
        waitingBot: false,
      };
    }

    // Set the history of conversations
    case 'chatbot.set-conversations-history': {
      return {
        ...currentState,
        history: action.payload,
      };
    }

    // Start a new conversation
    case 'chatbot.start-new-conversation': {
      return {
        ...currentState,
        conversationId: '',
        waitingBot: false,
        messages: [],
      };
    }

    // Switch to an existing conversation
    case 'chatbot.switch-conversation': {
      return {
        ...currentState,
        conversationId: action.payload.conversationId,
        waitingBot: false,
        messages: action.payload.messages.map((msg) => ({
          type: 'message',
          _id: generateId(),
          content: msg,
          metadata: {
            feedbackAllowed: true,
          },
        })),
      };
    }

    // User clicked on Like / Dislike button, so we create a new feedback interaction
    case 'feedback.requested': {
      // Create feedback action
      const newFeedback: UserFeedback = {
        ...action.payload,
        type: 'user-feedback',
        status: 'waiting-user-feedback',
        explanations: '',
        _id: generateId(),
      };
      // Update related message to disable further feedback
      const updatedMessages = currentState.messages.map((msg) => {
        if (msg.type === 'message' && msg.content.id === action.payload.message.id) {
          return {
            ...msg,
            metadata: {
              ...msg.metadata,
              feedbackAllowed: false,
            },
          };
        }
        return msg;
      });
      return {
        ...currentState,
        messages: [...updatedMessages, newFeedback],
      };
    }

    // The user cancelled providing feedback
    case 'feedback.cancelled': {
      // Remove the feedback interaction
      let updatedMessages = currentState.messages.filter((msg) => msg.type !== 'user-feedback' || msg._id !== action.payload._id);
      // Re-enable feedback on related message
      updatedMessages = updatedMessages.map((msg) => {
        if (msg.type === 'message' && msg.content.id === action.payload.message.id) {
          return {
            ...msg,
            metadata: {
              ...msg.metadata,
              feedbackAllowed: true,
            },
          };
        }
        return msg;
      });

      return {
        ...currentState,
        messages: updatedMessages,
      };
    }

    // The feedback has been submitted.
    case 'feedback.submitted': {
      // Remove the feedback interaction (we will re-inject it with updated status)
      const updatedMessages = currentState.messages.filter((msg) => msg.type !== 'user-feedback' || msg._id !== action.payload._id);

      // Define where we need to inject the feedback message, just after the related bot message
      const where = updatedMessages.findIndex((msg) => msg.type === 'message' && msg.content.id === action.payload.message.id);

      if (where === -1 || action.payload.status !== 'submitted') {
        // If we cannot find the related bot message (should not happen, but we never know...)
        // or if the feedback has not been successfully submitted, then just append the feedback interaction at the end
        updatedMessages.push({ ...action.payload });
      } else {
        // If submit was successful, then inject the feedback message just after the related bot message
        updatedMessages.splice(where + 1, 0, { ...action.payload });
      }

      return {
        ...currentState,
        messages: updatedMessages,
      };
    }

    default:
      return currentState;
  }
}

export function createInitialState(): BotConversationReducerState {
  return {
    conversationId: '',
    history: undefined,
    messages: [],
    waitingBot: false,
  };
}
